import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import SafeIcon from '../../components/common/SafeIcon';
import ResourceContentFields, { FieldLabel } from '../../components/resources/ResourceContentFields';
import { useAuth } from '../../context/AuthContext';
import {
  addResourceVersion,
  checkCanVersionResource,
  detectFileFormat,
  fetchResourceById,
} from '../../services/resources';

export default function ResourceVersionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();

  const [resource, setResource] = useState(null);
  const [canVersion, setCanVersion] = useState(false);
  const [loading, setLoading] = useState(true);
  const [versionMode, setVersionMode] = useState('add');
  const [contentType, setContentType] = useState('file');
  const [file, setFile] = useState(null);
  const [externalUrl, setExternalUrl] = useState('');
  const [linkKind, setLinkKind] = useState('google_docs');
  const [versionNote, setVersionNote] = useState('');
  const [versionLabel, setVersionLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadPhase, setUploadPhase] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      try {
        const row = await fetchResourceById(id);
        if (!active) return;
        if (!row) {
          setResource(null);
          setCanVersion(false);
          return;
        }
        const allowed = await checkCanVersionResource(row.uploadedBy);
        if (!active) return;
        setResource(row);
        setCanVersion(allowed);
        setContentType(row.type === 'link' ? 'link' : 'file');
      } catch (err) {
        console.error(err);
        if (active) {
          setResource(null);
          setCanVersion(false);
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => { active = false; };
  }, [id]);

  const validate = () => {
    if (contentType === 'file' && !file) return 'Selecciona un archivo';
    if (contentType === 'link' && !externalUrl.trim()) return 'Ingresa la URL del enlace';
    return null;
  };

  const buildContentPayload = () => {
    if (contentType === 'file') {
      const fileFormat = detectFileFormat(file);
      if (!fileFormat) {
        throw new Error('Formato no permitido. Usa PDF, DOCX o Markdown.');
      }

      return {
        type: 'file',
        file,
        fileFormat,
      };
    }

    return {
      type: 'link',
      externalUrl: externalUrl.trim(),
      linkKind,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setUploadPhase(contentType === 'file' ? 'Subiendo archivo…' : 'Guardando versión…');

    try {
      const content = buildContentPayload();

      setUploadPhase('Actualizando versión…');
      await addResourceVersion({
        resourceId: id,
        mode: versionMode,
        content,
        versionNote,
        versionLabel,
      });

      navigate(`/app/recursos/${id}`, {
        replace: true,
        state: { versionSuccess: true, versionMode },
      });
    } catch (err) {
      setError(err.message || 'No se pudo actualizar la versión');
    } finally {
      setSubmitting(false);
      setUploadPhase('');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center text-[var(--text-secondary)] text-sm font-bold uppercase tracking-widest">
        Cargando…
      </div>
    );
  }

  if (!resource || !canVersion) {
    return <Navigate to={`/app/recursos/${id}`} replace />;
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-10 pb-28">
      <div className="flex items-center gap-4 mb-8">
        <Link
          to={`/app/recursos/${id}`}
          className="w-10 h-10 rounded-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] flex items-center justify-center text-[var(--text-main)] hover:bg-[var(--border-subtle)] transition-colors"
        >
          <SafeIcon name="ArrowLeft" className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-[var(--text-main)] uppercase tracking-tight italic">
            Nueva versión
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1 line-clamp-2">
            {resource.title}
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-xl border border-red-400/40 bg-red-500/10 text-red-300 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        <section className="p-6 rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] space-y-4">
          <FieldLabel required>¿Cómo actualizar?</FieldLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              disabled={submitting}
              onClick={() => setVersionMode('add')}
              className={`p-4 rounded-xl border text-left transition-all ${
                versionMode === 'add'
                  ? 'border-brand-green/50 bg-brand-green/5'
                  : 'border-[var(--border-subtle)] hover:border-[var(--text-main)]/20'
              }`}
            >
              <p className="text-xs font-black uppercase tracking-widest text-[var(--text-main)] mb-1">
                Añadir versión
              </p>
              <p className="text-xs text-[var(--text-secondary)]">
                La nueva versión pasa a ser la actual. Las anteriores quedan en el historial.
              </p>
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={() => setVersionMode('replace')}
              className={`p-4 rounded-xl border text-left transition-all ${
                versionMode === 'replace'
                  ? 'border-brand-cyan/50 bg-brand-cyan/5'
                  : 'border-[var(--border-subtle)] hover:border-[var(--text-main)]/20'
              }`}
            >
              <p className="text-xs font-black uppercase tracking-widest text-[var(--text-main)] mb-1">
                Reemplazar
              </p>
              <p className="text-xs text-[var(--text-secondary)]">
                Elimina todas las versiones anteriores. Solo quedará la nueva.
              </p>
            </button>
          </div>
        </section>

        <ResourceContentFields
          contentType={contentType}
          onContentTypeChange={setContentType}
          file={file}
          onFileChange={setFile}
          externalUrl={externalUrl}
          onExternalUrlChange={setExternalUrl}
          linkKind={linkKind}
          onLinkKindChange={setLinkKind}
          versionNote={versionNote}
          onVersionNoteChange={setVersionNote}
          versionLabel={versionLabel}
          onVersionLabelChange={setVersionLabel}
          showVersionFields
          showVersionLabel
          submitting={submitting}
        />

        <button
          type="submit"
          disabled={submitting}
          className="w-full md:w-auto flex items-center justify-center gap-3 gfi-gradient-bg text-brand-navy px-10 py-4 rounded-full font-black text-xs uppercase tracking-widest transition-all hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100 shadow-xl"
        >
          {submitting ? (
            <>
              <SafeIcon name="Loader" className="w-5 h-5 animate-spin" />
              {uploadPhase || 'Guardando…'}
            </>
          ) : (
            <>
              <SafeIcon name="RefreshCw" className="w-5 h-5" />
              {versionMode === 'replace' ? 'Reemplazar versión' : 'Publicar nueva versión'}
            </>
          )}
        </button>
      </form>
    </div>
  );
}
